import { Image, Space, Tag, Tooltip } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import type { TicketAttachment } from "../../types/ticket";

interface AttachmentListProps {
  attachments: TicketAttachment[];
}

const formatSize = (n: number, t: (k: string, v?: any) => string) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return t("tickets.size_kb", { kb: (n / 1024).toFixed(1) });
  return t("tickets.size_mb", { mb: (n / 1024 / 1024).toFixed(1) });
};

const isImage = (contentType: string | null | undefined): boolean =>
  !!contentType && contentType.toLowerCase().startsWith("image/");

/**
 * Renderiza los adjuntos de un mensaje del hilo de incidencia.
 *
 * - Imágenes → miniatura (AntD `<Image>`) con preview al click (zoom/pan
 *   nativo de AntD). Antes se mostraba un `<Tag>` con el nombre y había
 *   que abrir el fichero en otra pestaña para saber qué era.
 * - Cualquier otro tipo (PDF, docx, etc.) → el `<Tag>` de siempre con
 *   `PaperClipOutlined` que abre el fichero en una pestaña nueva.
 *
 * Las URLs llegan firmadas con TTL corto desde el serializer del backend;
 * reutilizamos ese mismo URL para el thumb y para el preview — no hace
 * falta un endpoint separado de miniaturas.
 */
export const AttachmentList: React.FC<AttachmentListProps> = ({ attachments }) => {
  const { t } = useTranslation();

  if (attachments.length === 0) return null;

  const images = attachments.filter((a) => isImage(a.content_type) && a.url);
  const others = attachments.filter((a) => !isImage(a.content_type) || !a.url);

  return (
    <Space size={[8, 8]} wrap>
      {images.length > 0 && (
        // Grupo de imágenes: el preview permite navegar con flechas entre
        // todas las del mismo mensaje sin cerrar y reabrir el visor.
        <Image.PreviewGroup>
          <Space size={[8, 8]} wrap>
            {images.map((a) => (
              <Tooltip
                key={a.id}
                title={`${a.filename} (${formatSize(a.size, t)})`}
                placement="top"
              >
                <Image
                  src={a.url ?? ""}
                  alt={a.filename}
                  width={96}
                  height={96}
                  style={{
                    objectFit: "cover",
                    borderRadius: 4,
                    border: "1px solid #e8e8e8",
                  }}
                  preview={{ src: a.url ?? "" }}
                />
              </Tooltip>
            ))}
          </Space>
        </Image.PreviewGroup>
      )}
      {others.map((a) => (
        <a
          key={a.id}
          href={a.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Tag icon={<PaperClipOutlined />} style={{ cursor: "pointer" }}>
            {a.filename}{" "}
            <span style={{ color: "#8c8c8c", fontSize: 11 }}>
              ({formatSize(a.size, t)})
            </span>
          </Tag>
        </a>
      ))}
    </Space>
  );
};

export default AttachmentList;
